import isEqual from 'lodash/isEqual'

import * as Sentry from '@sentry/react'

import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { Service } from '../graphqlEnums'
import type { CreatePostInput } from './graphqlTypes'
import { mapToCreatePostInput } from './mapToCreatePostInput'
import type { MapperContext } from './types'

/**
 * Pre-flight payload comparison for the save-path cutover
 * (`composer-zustand-create-post`, CORE-7194): builds the legacy, Flux-only
 * `CreatePostInput` for the same draft/context and diffs it against the one
 * actually about to be sent (built from the mirrored `ChannelVariant` via
 * `channelVariantToCreatePostInput`), right before it ships — a save-content
 * mismatch is a bigger deal than the passive display-state drift
 * `channelDrift`/`omniDrift` catch, so this doesn't wait for a mirror write
 * to settle.
 *
 * `ctx` must NOT carry `channelVariant`: passing the base context (as built
 * in `buildCreatePostInputs` before the metadata-mapper call adds it) makes
 * `mapToCreatePostInput` take every migrated mapper's Flux-only branch, the
 * actual legacy behavior being compared against.
 *
 * Reports via the same `composer.bridge.drift` Sentry message/dashboard as
 * `omniDrift`/`channelDrift` (own `'payload'` fingerprint discriminator and
 * `track` tag), sampled the same ~1% in production, deterministic
 * `console.error` outside it. Only field names are logged or sent — never
 * post content.
 */
/**
 * Reports a failure to even build the comparison payload — a stronger
 * signal than a field-level drift, since it means the still-unramped path
 * would have thrown for this channel. Not sampled (unlike the drift check
 * below): a build failure should be rare enough that every occurrence is
 * worth seeing.
 */
export function reportCreatePostPayloadBuildFailure(
  channelId: string,
  service: Service,
  error: unknown,
): void {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] create-post payload build failed',
      `channelId=${channelId}`,
      `service=${service}`,
      error,
    )
    return
  }

  Sentry.captureException(error, {
    fingerprint: ['composer-bridge-drift', 'payload-build-failed', service],
    tags: {
      track: 'payload',
      kind: 'buildFailed',
      service,
    },
    extra: { channelId },
  })
}

export function checkCreatePostPayloadDrift(
  draft: Draft,
  ctx: MapperContext,
  channelId: string,
  service: Service,
  newInput: CreatePostInput,
): void {
  const isProduction = process.env.NODE_ENV === 'production'
  // 0.04 local sample x 0.25 Sentry client sampleRate (PublishSentryClient
  // applies it to every captured event) = ~1% of drift occurrences reported.
  // Keep in sync with omniDrift/channelDrift's rate.
  if (isProduction && Math.random() > 0.04) return

  const legacyInput = mapToCreatePostInput(draft, ctx)

  const driftedFields: string[] = []
  if (!isEqual(legacyInput.text, newInput.text)) driftedFields.push('text')
  if (!isEqual(legacyInput.mode, newInput.mode)) driftedFields.push('mode')
  if (!isEqual(legacyInput.schedulingType, newInput.schedulingType))
    driftedFields.push('schedulingType')
  if (!isEqual(legacyInput.assets, newInput.assets))
    driftedFields.push('assets')
  if (!isEqual(legacyInput.tagIds, newInput.tagIds))
    driftedFields.push('tagIds')
  if (!isEqual(legacyInput.aiAssisted, newInput.aiAssisted))
    driftedFields.push('aiAssisted')
  if (!isEqual(legacyInput.metadata, newInput.metadata))
    driftedFields.push('metadata')
  if (!isEqual(legacyInput.dueAt, newInput.dueAt)) driftedFields.push('dueAt')
  if (!isEqual(legacyInput.saveToDraft, newInput.saveToDraft))
    driftedFields.push('saveToDraft')
  if (!isEqual(legacyInput.ideaId, newInput.ideaId))
    driftedFields.push('ideaId')
  if (!isEqual(legacyInput.draftId, newInput.draftId))
    driftedFields.push('draftId')
  if (!isEqual(legacyInput.source, newInput.source))
    driftedFields.push('source')
  if (!isEqual(legacyInput.needsApproval, newInput.needsApproval))
    driftedFields.push('needsApproval')

  if (driftedFields.length === 0) return

  if (!isProduction) {
    // eslint-disable-next-line no-console
    console.error(
      '[composer.bridge] create-post payload drift detected',
      `channelId=${channelId}`,
      `service=${service}`,
      `fields=${driftedFields.join(',')}`,
    )
    return
  }

  const composerMode = AppStore.getOptions().composerMode ?? 'unknown'

  Sentry.addBreadcrumb({
    category: 'composer.bridge',
    level: 'warning',
    message: 'create-post payload drift',
    data: {
      track: 'payload',
      service,
      channelId,
      fields: driftedFields,
      composerMode,
    },
  })
  Sentry.captureEvent({
    message: 'composer.bridge.drift',
    level: 'warning',
    fingerprint: ['composer-bridge-drift', 'payload', service],
    tags: {
      track: 'payload',
      service,
      fields: driftedFields.join(','),
      composerMode,
    },
    extra: {
      channelId,
      driftedFieldCount: driftedFields.length,
    },
  })
}
